import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import {
  ApiResponse,
  TenantUserDto,
  TenantSearchDto,
} from '../../models';
import { TenantUserService } from '../../services/tenant-user.service';
import { TenantFeaturesService, TenantModule, TenantType } from '../../services/tenant-features.service';
import { parseModulesCsv, tenantTypeLabelKey, TENANT_MODULE_OPTIONS } from '../../services/tenant-modules';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { apiErrorMessage } from '../../courses/lms-utils';

/**
 * Tenant Management landing page.
 * Admin / TenantAdmin only (gated in the sidebar and enforced by the backend
 * TenantsController, which is restricted to Administrator + TenantAdmin).
 *
 * Provides a tenant-focused table (name / URL / admin) with search, pagination,
 * and create / edit / delete. Creating a tenant also provisions its first
 * tenant-admin user; editing updates the tenant profile and its admin contact.
 */
@Component({
  selector: 'app-tenant-management',
  templateUrl: './tenant-management.component.html',
  styleUrl: './tenant-management.component.scss',
})
export class TenantManagementComponent implements OnInit, OnDestroy {
  @ViewChild('confirmDialog') confirmDialog!: ConfirmDialogComponent;

  form: FormGroup;
  tenants: TenantUserDto[] = [];
  selected: TenantUserDto | null = null;

  isLoading = true;
  isModalShow = false;
  isEditMode = false;
  totalItems = 0;

  searchTerm = '';
  private editTenantId = '';
  private editTeacherId = '';
  /** The list request in flight: a newer search cancels it, so a slow older page can't overwrite the newer result. */
  private loadSub?: Subscription;

  search: TenantSearchDto = {
    pageNumber: 1,
    pageSize: 10,
    sortingByColumn: 'nameEn',
    sortingType: 1,
    isExport: false,
    nameAr: '',
    nameEn: '',
  };

  // ── Modules dialog (platform Administrator) ─────────────────────────────
  modulesTarget: TenantUserDto | null = null;
  modulesType: TenantType = TenantType.Mixed;
  modulesValue: TenantModule[] = [];
  isSavingModules = false;

  constructor(
    private fb: FormBuilder,
    private tenantSrv: TenantUserService,
    private toastr: ToastrService,
    private auth: AuthService,
    private features: TenantFeaturesService,
    private translate: TranslateService,
    private language: LanguageService
  ) {
    this.form = this.fb.group(
      {
        // Tenant fields
        nameAr: ['', [Validators.required, Validators.minLength(3)]],
        nameEn: ['', [Validators.required, Validators.minLength(3)]],
        url: ['', [Validators.required, Validators.minLength(3)]],
        descriptionAr: [''],
        descriptionEn: [''],
        // Tenant-admin fields
        fullNameAr: ['', [Validators.required, Validators.minLength(3)]],
        fullNameEn: ['', [Validators.required, Validators.minLength(3)]],
        email: ['', [Validators.required, Validators.email]],
        phoneNumber: [
          '',
          [Validators.required, Validators.pattern('^\\+?[0-9]{10,15}$')],
        ],
        dateOfBirth: ['', [Validators.required]],
        genderId: ['', [Validators.required]],
        userName: ['', [Validators.required, Validators.minLength(3)]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.load(1);
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  /** Keeps rows (and their buttons) in the DOM when a reload returns the same tenants. */
  trackTenant(_: number, item: TenantUserDto): string {
    return item.tenant.id ?? item.tenant.url ?? item.tenant.nameEn;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.search.pageSize));
  }

  // ── Data ────────────────────────────────────────────────────────────────
  load(page: number): void {
    this.isLoading = true;
    this.search = {
      ...this.search,
      pageNumber: page,
      nameEn: this.searchTerm,
      nameAr: this.searchTerm,
    };

    this.loadSub?.unsubscribe();
    this.loadSub = this.tenantSrv.getTenantUsers(this.search).subscribe({
      next: (res: ApiResponse<TenantUserDto[]>) => {
        if (res.isSuccess && res.value) {
          this.tenants = res.value;
          this.totalItems = res.totalCount;
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error('Failed to load tenants.');
        this.isLoading = false;
      },
    });
  }

  applySearch(): void {
    this.load(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.search.pageNumber) {
      return;
    }
    this.load(page);
  }

  // ── Modal ───────────────────────────────────────────────────────────────
  /** Creating and deleting tenants is reserved for the platform Administrator (the API enforces it too). */
  get isPlatformAdmin(): boolean {
    return this.auth.hasAnyRole('Administrator');
  }

  openAdd(): void {
    if (!this.isPlatformAdmin) return;
    this.isEditMode = false;
    this.editTenantId = '';
    this.editTeacherId = '';
    this.selected = null;
    this.form.reset();
    this.setPasswordRequired(true);
    this.isModalShow = true;
  }

  openEdit(item: TenantUserDto): void {
    this.isEditMode = true;
    this.selected = item;
    this.editTenantId = item.tenant.id ?? '';
    this.editTeacherId = item.teacher.id ?? '';
    this.setPasswordRequired(false);
    this.form.patchValue({
      nameAr: item.tenant.nameAr,
      nameEn: item.tenant.nameEn,
      url: item.tenant.url,
      descriptionAr: item.tenant.descriptionAr,
      descriptionEn: item.tenant.descriptionEn,
      fullNameAr: item.teacher.fullNameAr,
      fullNameEn: item.teacher.fullNameEn,
      email: item.teacher.email,
      phoneNumber: item.teacher.phoneNumber,
      dateOfBirth: item.teacher.dateOfBirth,
      genderId: item.teacher.genderId,
      userName: item.teacher.userName,
    });
    this.isModalShow = true;
  }

  closeModal(): void {
    this.isModalShow = false;
    this.selected = null;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    const payload = this.buildPayload();
    const request$ = this.editTenantId
      ? this.tenantSrv.updateTenantUser(this.editTenantId, payload)
      : this.tenantSrv.addTenantUser(payload);

    request$.subscribe({
      next: (res: ApiResponse<TenantUserDto>) => {
        if (res.isSuccess) {
          this.toastr.success(
            this.editTenantId
              ? 'Tenant updated successfully.'
              : 'Tenant created successfully.'
          );
          this.closeModal();
          this.load(1);
        } else {
          this.toastr.error('Unexpected response from the server.');
          this.isLoading = false;
        }
      },
      error: () => {
        this.toastr.error('Failed to save tenant.');
        this.isLoading = false;
      },
    });
  }

  // ── Type & modules ──────────────────────────────────────────────────────
  /** i18n key of a tenant's organisation type. */
  typeLabelKey(item: TenantUserDto): string {
    return tenantTypeLabelKey(item.tenant.tenantType ?? TenantType.Mixed);
  }

  /** A tenant's stored modules; an unset value means every module (the column default). */
  tenantModules(item: TenantUserDto): TenantModule[] {
    const csv = item.tenant.enabledModules;
    return csv == null ? TENANT_MODULE_OPTIONS.map((o) => o.module) : parseModulesCsv(csv);
  }

  moduleLabelKey(module: TenantModule): string {
    return TENANT_MODULE_OPTIONS.find((o) => o.module === module)?.labelKey ?? module;
  }

  openModules(item: TenantUserDto): void {
    if (!this.isPlatformAdmin || !item.tenant.id) return;
    this.modulesTarget = item;
    this.modulesType = (item.tenant.tenantType ?? TenantType.Mixed) as TenantType;
    this.modulesValue = this.tenantModules(item);
  }

  closeModules(): void {
    this.modulesTarget = null;
    this.isSavingModules = false;
  }

  saveModules(): void {
    const target = this.modulesTarget;
    if (!target?.tenant.id || this.modulesValue.length === 0) return;
    this.isSavingModules = true;
    this.features
      .updateModules({ tenantType: this.modulesType, modules: this.modulesValue, tenantId: target.tenant.id })
      .subscribe({
        next: (res) => {
          this.isSavingModules = false;
          if (!res?.isSuccess) {
            this.toastr.error(apiErrorMessage(res, this.language.current, this.translate.instant('tenantModules.SaveFailed')));
            return;
          }
          target.tenant.tenantType = this.modulesType;
          target.tenant.enabledModules = (res.value?.configuredModules ?? this.modulesValue).join(',');
          const blocked = (res.value?.planBlockedModules ?? []).filter((m) => this.modulesValue.includes(m));
          if (blocked.length) {
            this.toastr.warning(this.translate.instant('tenantModules.SavedButBlocked', {
              modules: blocked.map((m) => this.translate.instant(this.moduleLabelKey(m))).join(this.language.current === 'ar' ? '، ' : ', '),
            }));
          } else {
            this.toastr.success(this.translate.instant('tenantModules.SavedForTenant', { name: target.tenant.nameEn }));
          }
          // Editing the tenant the admin is signed in to: refresh their own menu too.
          if ((target.tenant.id ?? '').toLowerCase() === (localStorage.getItem('tenantId') ?? '').toLowerCase()) {
            this.features.load(true).subscribe();
          }
          this.closeModules();
        },
        error: (err) => {
          this.isSavingModules = false;
          this.toastr.error(apiErrorMessage(err, this.language.current, this.translate.instant('tenantModules.SaveFailed')));
        },
      });
  }

  // ── Delete ──────────────────────────────────────────────────────────────
  confirmDelete(item: TenantUserDto): void {
    this.selected = item;
    this.confirmDialog.openModal();
  }

  onDeleteConfirmed(confirmed: boolean): void {
    if (!confirmed || !this.selected?.tenant.id) return;
    this.isLoading = true;
    const name = this.selected.tenant.nameEn;
    this.tenantSrv.deleteTenantUser(this.selected.tenant.id).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.toastr.success(`Tenant "${name}" deleted.`);
          this.load(1);
        } else {
          this.toastr.error('Unexpected response from the server.');
          this.isLoading = false;
        }
      },
      error: () => {
        this.toastr.error(`Failed to delete tenant "${name}".`);
        this.isLoading = false;
      },
    });
  }

  // ── Helpers ─────────────────────────────────────────────────────────────
  private setPasswordRequired(required: boolean): void {
    const pw = this.form.get('password');
    const cpw = this.form.get('confirmPassword');
    if (required) {
      pw?.setValidators([Validators.required, Validators.minLength(6)]);
      cpw?.setValidators([Validators.required]);
    } else {
      pw?.clearValidators();
      cpw?.clearValidators();
    }
    pw?.updateValueAndValidity();
    cpw?.updateValueAndValidity();
  }

  private passwordMatchValidator(form: FormGroup) {
    const pw = form.get('password')?.value;
    const cpw = form.get('confirmPassword')?.value;
    if (!pw && !cpw) return null;
    return pw === cpw ? null : { mismatch: true };
  }

  private buildPayload(): any {
    const v = this.form.value;
    if (this.editTenantId) {
      return {
        tenant: {
          id: this.editTenantId,
          nameAr: v.nameAr,
          nameEn: v.nameEn,
          url: v.url,
          descriptionAr: v.descriptionAr,
          descriptionEn: v.descriptionEn,
        },
        teacher: {
          id: this.editTeacherId,
          fullNameAr: v.fullNameAr,
          fullNameEn: v.fullNameEn,
          genderId: v.genderId ? parseInt(v.genderId, 10) : null,
          phoneNumber: v.phoneNumber,
          dateOfBirth: v.dateOfBirth,
        },
      };
    }
    return {
      tenant: {
        nameAr: v.nameAr,
        nameEn: v.nameEn,
        url: v.url,
        descriptionAr: v.descriptionAr,
        descriptionEn: v.descriptionEn,
      },
      teacher: {
        userName: v.userName,
        email: v.email,
        fullNameAr: v.fullNameAr,
        fullNameEn: v.fullNameEn,
        genderId: v.genderId ? parseInt(v.genderId, 10) : null,
        phoneNumber: v.phoneNumber,
        dateOfBirth: v.dateOfBirth,
        password: v.password,
      },
    };
  }
}
